// CostAnalysisReport.jsx - Cost Analysis Report with Revenue, Profit & Auth Status
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import secureAxios from "../../utils/secureAxios";
import { auditApi } from "../../utils/api";
import { toast } from "react-toastify";

const CostAnalysisReport = () => {
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    customer_id: "",
    consumer_id: "",
    user_id: "",
    service_id: "",
    start_date: "",
    end_date: "",
    group_by: "authorization", // authorization, consumer, customer, user, service, month
  });

  // Dropdown options
  const [customers, setCustomers] = useState([]);
  const [consumers, setConsumers] = useState([]);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);

  const { user, tenant_id } = useAuth();

  // Group by options
  const groupByOptions = [
    { value: "authorization", label: "By Authorization" },
    { value: "consumer", label: "By Consumer" },
    { value: "customer", label: "By Customer" },
    { value: "user", label: "By Job Coach" },
    { value: "service", label: "By Service" },
    { value: "month", label: "By Month" },
  ];

  // Log audit event
  const logAuditEvent = useCallback(
    async (eventDetails) => {
      try {
        await auditApi.logEntry({
          ...eventDetails,
          tenant_id: tenant_id,
          user_id: user?.user_id,
        });
      } catch (err) {
        console.error("Failed to log audit event:", err);
      }
    },
    [tenant_id, user?.user_id]
  );

  // Load report data
  const loadReport = useCallback(
    async (showToast = false) => {
      try {
        setLoading(true);

        const params = new URLSearchParams();
        if (filters.customer_id)
          params.append("customer_id", filters.customer_id);
        if (filters.consumer_id)
          params.append("consumer_id", filters.consumer_id);
        if (filters.user_id) params.append("user_id", filters.user_id);
        if (filters.service_id) params.append("service_id", filters.service_id);
        if (filters.start_date) params.append("start_date", filters.start_date);
        if (filters.end_date) params.append("end_date", filters.end_date);
        if (filters.group_by) params.append("group_by", filters.group_by);

        const response = await secureAxios.get(
          `/api/business-reports/cost-analysis?${params}`
        );

        if (response.data.success) {
          setReportData(response.data.data);
          setSummary(response.data.summary);

          // Log audit event for report generation
          await auditApi.logEntry({
            action: "GENERATE_COST_ANALYSIS_REPORT",
            entity_type: "REPORT",
            entity_id: null,
            entity_identifier: `Cost Analysis Report - ${filters.group_by}`,
            contains_phi: true,
            action_status: "SUCCESS",
            purpose_of_use: "OPERATIONS",
          });

          if (showToast) {
            toast.success(
              `Report generated: ${response.data.data.length} records found`
            );
          }
        } else {
          throw new Error("Failed to generate report");
        }
      } catch (err) {
        console.error("Error loading cost analysis report:", err);
        if (showToast) {
          toast.error("Failed to load cost analysis report");
        }
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    },
    [filters]
  );

  // Load filter options on component mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        // Load customers
        const customerRes = await secureAxios.get("/api/customers", {
          params: { tenant_id: tenant_id },
        });
        setCustomers(customerRes.data || []);

        // Load consumers
        const consumerRes = await secureAxios.get("/api/consumers", {
          params: {
            tenant_id: tenant_id,
            user_id: user?.user_id,
            role: user?.role,
            tenant_type: user?.tenant_type,
          },
        });
        setConsumers(consumerRes.data || []);

        // Load users (job coaches)
        const userRes = await secureAxios.get("/api/users", {
          params: { tenant_id: tenant_id },
        });
        const jobCoaches = (userRes.data || []).filter(
          (u) =>
            u.role?.toLowerCase() === "job coach" ||
            u.role?.toLowerCase() === "admin"
        );
        setUsers(jobCoaches);

        // Load services (include customer name for disambiguation)
        const serviceRes = await secureAxios.get("/api/services", {
          params: { tenant_id: tenant_id },
        });
        // Sort by service name, then customer name for easier reading
        const sortedServices = (serviceRes.data || []).sort((a, b) => {
          const nameCompare = (a.service_name || "").localeCompare(
            b.service_name || ""
          );
          if (nameCompare !== 0) return nameCompare;
          return (a.customer_name || "").localeCompare(b.customer_name || "");
        });
        setServices(sortedServices);
      } catch (err) {
        console.error("Error loading filter options:", err);
        toast.error("Failed to load filter options");
      }
    };

    if (tenant_id) {
      loadFilterOptions();
    }
  }, [tenant_id, user]);

  // Auto-load report when filters change
  useEffect(() => {
    if (tenant_id) {
      const timeoutId = setTimeout(() => {
        loadReport(false);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [tenant_id, loadReport]);

  // Export to CSV
  const exportToCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.customer_id)
        params.append("customer_id", filters.customer_id);
      if (filters.consumer_id)
        params.append("consumer_id", filters.consumer_id);
      if (filters.user_id) params.append("user_id", filters.user_id);
      if (filters.service_id) params.append("service_id", filters.service_id);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.group_by) params.append("group_by", filters.group_by);

      const response = await secureAxios.get(
        `/api/business-reports/cost-analysis/export?${params}`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const timestamp = new Date().toISOString().split("T")[0];
      link.download = `cost_analysis_report_${timestamp}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Log export audit event
      await auditApi.logEntry({
        action: "EXPORT_COST_ANALYSIS_REPORT",
        entity_type: "REPORT",
        entity_id: null,
        entity_identifier: `Cost Analysis CSV Export - ${timestamp}`,
        contains_phi: true,
        action_status: "SUCCESS",
        purpose_of_use: "OPERATIONS",
      });

      toast.success("Report exported successfully!");
    } catch (err) {
      console.error("Error exporting report:", err);
      toast.error("Failed to export report");
    }
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      customer_id: "",
      consumer_id: "",
      user_id: "",
      service_id: "",
      start_date: "",
      end_date: "",
      group_by: "authorization",
    });
  };

  // Refresh report
  const handleRefresh = () => {
    loadReport(true);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  // Format number
  const formatNumber = (num, decimals = 2) => {
    return Number(num || 0).toFixed(decimals);
  };

  // Get profit color class
  const getProfitClass = (profit) => {
    if (profit > 0) return "text-success";
    if (profit < 0) return "text-danger";
    return "text-muted";
  };

  // Get auth status badge
  const getAuthStatusBadge = (status) => {
    switch (status) {
      case "Complete":
        return "bg-success";
      case "In Progress":
        return "bg-warning text-dark";
      case "Partial":
        return "bg-info";
      default:
        return "bg-secondary";
    }
  };

  return (
    <div className="container-fluid">
      {/* Header with Actions */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-currency-dollar me-2"></i>
            Cost Analysis Report
          </h2>
          <p className="text-muted mb-0">
            Analyze costs, revenue, and profit by consumer, customer, job coach,
            service, or time period
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button
            className="btn btn-outline-secondary"
            onClick={() => setFiltersOpen(!filtersOpen)}
          >
            <i className={`bi bi-funnel${filtersOpen ? "-fill" : ""} me-1`}></i>
            Filters
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={handleRefresh}
            disabled={loading}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            Refresh
          </button>
          <button
            className="btn btn-success"
            onClick={exportToCSV}
            disabled={loading || reportData.length === 0}
          >
            <i className="bi bi-download me-1"></i>
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {filtersOpen && (
        <div className="card mb-4">
          <div className="card-body">
            <div className="row g-3">
              {/* Group By */}
              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label">Group By</label>
                <select
                  className="form-select"
                  value={filters.group_by}
                  onChange={(e) =>
                    handleFilterChange("group_by", e.target.value)
                  }
                >
                  {groupByOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Customer Filter */}
              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label">Customer</label>
                <select
                  className="form-select"
                  value={filters.customer_id}
                  onChange={(e) =>
                    handleFilterChange("customer_id", e.target.value)
                  }
                >
                  <option value="">All Customers</option>
                  {customers.map((c) => (
                    <option key={c.customer_id} value={c.customer_id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Consumer Filter */}
              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label">Consumer</label>
                <select
                  className="form-select"
                  value={filters.consumer_id}
                  onChange={(e) =>
                    handleFilterChange("consumer_id", e.target.value)
                  }
                >
                  <option value="">All Consumers</option>
                  {consumers.map((c) => (
                    <option key={c.consumer_id} value={c.consumer_id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Job Coach Filter */}
              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label">Job Coach</label>
                <select
                  className="form-select"
                  value={filters.user_id}
                  onChange={(e) =>
                    handleFilterChange("user_id", e.target.value)
                  }
                >
                  <option value="">All Job Coaches</option>
                  {users.map((u) => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Service Filter */}
              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label">Service</label>
                <select
                  className="form-select"
                  value={filters.service_id}
                  onChange={(e) =>
                    handleFilterChange("service_id", e.target.value)
                  }
                >
                  <option value="">All Services</option>
                  {services.map((s) => (
                    <option key={s.service_id} value={s.service_id}>
                      {s.service_name}
                      {s.customer_name ? ` - ${s.customer_name}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Range */}
              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.start_date}
                  onChange={(e) =>
                    handleFilterChange("start_date", e.target.value)
                  }
                />
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.end_date}
                  onChange={(e) =>
                    handleFilterChange("end_date", e.target.value)
                  }
                />
              </div>

              {/* Clear Filters Button */}
              <div className="col-12 col-md-6 col-lg-3 d-flex align-items-end">
                <button
                  className="btn btn-outline-secondary w-100"
                  onClick={clearFilters}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {!initialLoad && !loading && summary && (
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-4 col-xl">
            <div className="card bg-secondary text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">Hours</h6>
                <h4 className="card-title mb-0">
                  {formatNumber(summary.total_hours || 0, 1)}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl">
            <div className="card bg-info text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">Miles</h6>
                <h4 className="card-title mb-0">
                  {formatNumber(summary.total_mileage || 0, 0)}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl">
            <div className="card bg-danger h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 text-white opacity-75 small">Cost</h6>
                <h4 className="card-title mb-0 text-white">
                  {formatCurrency(summary.total_cost || 0)}
                </h4>
                <div className="d-flex justify-content-center gap-2 mt-1">
                  <span className="badge bg-light text-success" title="Paid to employees">
                    <i className="bi bi-check-circle me-1"></i>
                    {formatCurrency(summary.total_cost_paid || 0)}
                  </span>
                  <span className="badge bg-light text-danger" title="Not yet paid">
                    <i className="bi bi-clock me-1"></i>
                    {formatCurrency(summary.total_cost_unpaid || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl">
            <div className="card bg-primary h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 text-white opacity-75 small">Revenue</h6>
                <h4 className="card-title mb-0 text-white">
                  {formatCurrency(summary.total_revenue || 0)}
                </h4>
                <div className="d-flex justify-content-center gap-2 mt-1">
                  <span className="badge bg-light text-success" title="Invoiced to customer">
                    <i className="bi bi-receipt me-1"></i>
                    {formatCurrency(summary.total_revenue_invoiced || 0)}
                  </span>
                  <span className="badge bg-light text-warning" title="Not yet invoiced">
                    <i className="bi bi-hourglass me-1"></i>
                    {formatCurrency(summary.total_revenue_not_invoiced || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl">
            <div
              className={`card h-100 ${
                (summary.total_profit || 0) >= 0 ? "bg-success" : "bg-warning"
              } text-white`}
            >
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">Profit</h6>
                <h4 className="card-title mb-0">
                  {formatCurrency(summary.total_profit || 0)}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl">
            <div
              className={`card h-100 ${
                (summary.total_margin || 0) >= 20
                  ? "bg-success"
                  : (summary.total_margin || 0) >= 0
                  ? "bg-warning"
                  : "bg-danger"
              } text-white`}
            >
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">Margin</h6>
                <h4 className="card-title mb-0">
                  {formatNumber(summary.total_margin || 0, 1)}%
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl">
            <div className="card bg-dark text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">Auths</h6>
                <h4 className="card-title mb-0">
                  <span className="text-success">
                    {summary.complete_auths || 0}
                  </span>
                  {" / "}
                  <span className="text-warning">
                    {summary.incomplete_auths || 0}
                  </span>
                </h4>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Generating report...</p>
        </div>
      )}

      {/* Report Data Table - Desktop */}
      {!loading && reportData.length > 0 && (
        <>
          <div className="d-none d-lg-block">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                  <strong>
                    Cost Analysis -{" "}
                    {
                      groupByOptions.find((g) => g.value === filters.group_by)
                        ?.label
                    }
                  </strong>
                  <span className="badge bg-secondary ms-2">
                    {reportData.length} records
                  </span>
                </div>
                <div className="small text-muted">
                  <span className="text-success me-2"><i className="bi bi-check-circle-fill"></i> Complete</span>
                  <span className="text-warning"><i className="bi bi-clock"></i> In Progress</span>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-striped table-hover mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Auth#</th>
                      <th>Details</th>
                      <th className="text-end">Hours</th>
                      <th className="text-end">Miles</th>
                      <th className="text-end">Cost</th>
                      <th className="text-end">Revenue</th>
                      <th className="text-end">Profit</th>
                      <th className="text-end">Margin</th>
                      <th className="text-center">Auth Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((row, index) => (
                      <tr key={index}>
                        <td className="fw-bold">{row.primary_name || "-"}</td>
                        <td className="text-muted small">
                          {filters.group_by === "authorization" ? (
                            <>
                              <div>{row.secondary_name}</div>
                              <div className="text-info">
                                {row.customer_name}
                              </div>
                              <div className="fst-italic">
                                {row.service_name}
                              </div>
                            </>
                          ) : (
                            <>
                              {row.secondary_name || "-"}
                              {row.service_count > 0 && (
                                <span className="ms-2 badge bg-light text-dark">
                                  {row.service_count} services
                                </span>
                              )}
                            </>
                          )}
                        </td>
                        <td className="text-end">
                          {formatNumber(row.total_hours, 1)}
                        </td>
                        <td className="text-end">
                          {formatNumber(row.total_mileage, 0)}
                        </td>
                        <td className="text-end">
                          <div>{formatCurrency(row.total_cost)}</div>
                          <div className="small">
                            <span className="text-success" title="Paid">
                              <i className="bi bi-check-circle me-1"></i>
                              {formatCurrency(row.cost_paid || 0)}
                            </span>
                            {" / "}
                            <span className="text-danger" title="Unpaid">
                              <i className="bi bi-clock me-1"></i>
                              {formatCurrency(row.cost_unpaid || 0)}
                            </span>
                          </div>
                        </td>
                        <td className="text-end">
                          <div>{formatCurrency(row.revenue)}</div>
                          <div className="small">
                            <span className="text-success" title="Invoiced">
                              <i className="bi bi-receipt me-1"></i>
                              {formatCurrency(row.revenue_invoiced || 0)}
                            </span>
                            {" / "}
                            <span className="text-warning" title="Not Invoiced">
                              <i className="bi bi-hourglass me-1"></i>
                              {formatCurrency(row.revenue_not_invoiced || 0)}
                            </span>
                          </div>
                        </td>
                        <td
                          className={`text-end fw-bold ${getProfitClass(
                            row.profit
                          )}`}
                        >
                          {formatCurrency(row.profit)}
                        </td>
                        <td
                          className={`text-end ${
                            (row.margin || 0) >= 20
                              ? "text-success"
                              : (row.margin || 0) >= 0
                              ? "text-warning"
                              : "text-danger"
                          }`}
                        >
                          {formatNumber(row.margin || 0, 1)}%
                        </td>
                        <td className="text-center">
                          <span
                            className={`badge ${getAuthStatusBadge(
                              row.auth_status
                            )}`}
                          >
                            {row.auth_status || "N/A"}
                          </span>
                          <div className="small text-muted">
                            {row.complete_auths || 0}<i className="bi bi-check text-success"></i> /{" "}
                            {row.incomplete_auths || 0}<i className="bi bi-circle text-warning"></i>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-secondary">
                    <tr className="fw-bold">
                      <td colSpan={2}>Totals</td>
                      <td className="text-end">
                        {formatNumber(summary.total_hours, 1)}
                      </td>
                      <td className="text-end">
                        {formatNumber(summary.total_mileage, 0)}
                      </td>
                      <td className="text-end">
                        <div>{formatCurrency(summary.total_cost)}</div>
                        <div className="small fw-normal">
                          <span className="text-success">
                            <i className="bi bi-check-circle me-1"></i>
                            {formatCurrency(summary.total_cost_paid || 0)}
                          </span>
                          {" / "}
                          <span className="text-danger">
                            <i className="bi bi-clock me-1"></i>
                            {formatCurrency(summary.total_cost_unpaid || 0)}
                          </span>
                        </div>
                      </td>
                      <td className="text-end">
                        <div>{formatCurrency(summary.total_revenue)}</div>
                        <div className="small fw-normal">
                          <span className="text-success">
                            <i className="bi bi-receipt me-1"></i>
                            {formatCurrency(summary.total_revenue_invoiced || 0)}
                          </span>
                          {" / "}
                          <span className="text-warning">
                            <i className="bi bi-hourglass me-1"></i>
                            {formatCurrency(summary.total_revenue_not_invoiced || 0)}
                          </span>
                        </div>
                      </td>
                      <td
                        className={`text-end ${getProfitClass(
                          summary.total_profit
                        )}`}
                      >
                        {formatCurrency(summary.total_profit)}
                      </td>
                      <td
                        className={`text-end ${
                          (summary.total_margin || 0) >= 20
                            ? "text-success"
                            : (summary.total_margin || 0) >= 0
                            ? "text-warning"
                            : "text-danger"
                        }`}
                      >
                        {formatNumber(summary.total_margin || 0, 1)}%
                      </td>
                      <td className="text-center">
                        {summary.complete_auths || 0}<i className="bi bi-check text-success"></i> /{" "}
                        {summary.incomplete_auths || 0}<i className="bi bi-circle text-warning"></i>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="d-block d-lg-none">
            <div className="row g-2">
              {reportData.map((row, index) => (
                <div key={index} className="col-12">
                  <div className="card">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h6 className="mb-0 text-primary fw-bold">
                            {row.primary_name || "-"}
                          </h6>
                          {filters.group_by === "authorization" ? (
                            <>
                              <div className="small text-muted">
                                {row.secondary_name}
                              </div>
                              <div className="small text-info">
                                {row.customer_name}
                              </div>
                              <div className="small fst-italic">
                                {row.service_name}
                              </div>
                            </>
                          ) : (
                            <small className="text-muted">
                              {row.secondary_name}
                            </small>
                          )}
                        </div>
                        <span
                          className={`badge ${getAuthStatusBadge(
                            row.auth_status
                          )}`}
                        >
                          {row.auth_status || "N/A"}
                        </span>
                      </div>

                      <div className="row g-2 text-center mb-2">
                        <div className="col-4">
                          <div className="bg-light p-2 rounded">
                            <div className="small text-muted">Hours</div>
                            <div className="fw-bold">
                              {formatNumber(row.total_hours, 1)}
                            </div>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="bg-light p-2 rounded">
                            <div className="small text-muted">Miles</div>
                            <div className="fw-bold">
                              {formatNumber(row.total_mileage, 0)}
                            </div>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="bg-light p-2 rounded">
                            <div className="small text-muted">Services</div>
                            <div className="fw-bold">
                              {row.service_count || 0}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row g-2 text-center">
                        <div className="col-3">
                          <div className="bg-danger bg-opacity-10 p-2 rounded">
                            <div className="small text-danger">Cost</div>
                            <div className="fw-bold text-danger small">
                              {formatCurrency(row.total_cost)}
                            </div>
                          </div>
                        </div>
                        <div className="col-3">
                          <div className="bg-primary bg-opacity-10 p-2 rounded">
                            <div className="small text-primary">Revenue</div>
                            <div className="fw-bold text-primary small">
                              {formatCurrency(row.revenue)}
                            </div>
                          </div>
                        </div>
                        <div className="col-3">
                          <div
                            className={`p-2 rounded ${
                              row.profit >= 0
                                ? "bg-success bg-opacity-10"
                                : "bg-warning bg-opacity-10"
                            }`}
                          >
                            <div
                              className={`small ${getProfitClass(row.profit)}`}
                            >
                              Profit
                            </div>
                            <div
                              className={`fw-bold small ${getProfitClass(
                                row.profit
                              )}`}
                            >
                              {formatCurrency(row.profit)}
                            </div>
                          </div>
                        </div>
                        <div className="col-3">
                          <div
                            className={`p-2 rounded ${
                              (row.margin || 0) >= 20
                                ? "bg-success bg-opacity-10"
                                : (row.margin || 0) >= 0
                                ? "bg-warning bg-opacity-10"
                                : "bg-danger bg-opacity-10"
                            }`}
                          >
                            <div
                              className={`small ${
                                (row.margin || 0) >= 20
                                  ? "text-success"
                                  : (row.margin || 0) >= 0
                                  ? "text-warning"
                                  : "text-danger"
                              }`}
                            >
                              Margin
                            </div>
                            <div
                              className={`fw-bold small ${
                                (row.margin || 0) >= 20
                                  ? "text-success"
                                  : (row.margin || 0) >= 0
                                  ? "text-warning"
                                  : "text-danger"
                              }`}
                            >
                              {formatNumber(row.margin || 0, 1)}%
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* No Data Message */}
      {!initialLoad && !loading && reportData.length === 0 && (
        <div className="text-center py-5">
          <i className="bi bi-bar-chart fa-3x text-muted mb-3"></i>
          <h5 className="text-muted">No data found</h5>
          <p className="text-muted">
            Try adjusting your filters or date range to see cost analysis data.
          </p>
        </div>
      )}
    </div>
  );
};

export default CostAnalysisReport;